// Моки слайса `user` для разработки без бэка (VITE_API_MOCKS=true, см. docs/data-layer.md §10).
import type {
  AcceptTermsRequest,
  AcceptTermsResponse,
  CityDto,
  CompleteOnboardingResponse,
  DeleteMeResponse,
  InvitePreviewResponse,
  MeResponse,
  SearchCitiesResponse,
  UpdateBirthRequest,
  UpdateMeRequest,
  UpdateSettingsRequest,
  UpdateVacationRequest,
} from '@lunya/contracts'
import { API_PATHS, TERMS_VERSION } from '@lunya/contracts'

import { registerMock } from '~/shared/api'
import { DEV_USER_NAME } from '~/shared/config'
import { LocalStorage } from '~/shared/lib/persist'

/**
 * «Серверное» состояние онбординга в дев-режиме без бэка.
 *
 * Живёт в LocalStorage, а не в переменной модуля: мок изображает базу, а она переживает
 * перезагрузку страницы — иначе онбординг показывался бы при каждом обновлении.
 * Ключ отдельный от оптимистичного кэша сессии (`lunya.onboarding_seen`) специально:
 * это две разные вещи — «что помнит сервер» и «что успел запомнить клиент».
 */
const mockOnboardedAtStorage = new LocalStorage<string | null>('lunya.mock.onboarded_at')

/**
 * «Серверная» отметка о принятой редакции оферты. Тоже в LocalStorage и тоже отдельным ключом:
 * шторка акцепта обязана вести себя как в проде — показаться один раз и больше не возвращаться,
 * в том числе после перезагрузки страницы.
 */
const mockTermsVersionStorage = new LocalStorage<string | null>('lunya.mock.terms_version')

const MOCK_CITIES: readonly CityDto[] = [
  {
    id: 'city_msk',
    name: 'Москва',
    region: null,
    country: 'Россия',
    lat: 55.7558,
    lon: 37.6173,
    tz: 'Europe/Moscow',
  },
  {
    id: 'city_spb',
    name: 'Санкт-Петербург',
    region: null,
    country: 'Россия',
    lat: 59.9311,
    lon: 30.3609,
    tz: 'Europe/Moscow',
  },
  {
    id: 'city_krsk',
    name: 'Красноярск',
    region: 'Красноярский край',
    country: 'Россия',
    lat: 56.0184,
    lon: 92.8672,
    tz: 'Asia/Krasnoyarsk',
  },
  {
    id: 'city_ekb',
    name: 'Екатеринбург',
    region: 'Свердловская область',
    country: 'Россия',
    lat: 56.8389,
    lon: 60.6057,
    tz: 'Asia/Yekaterinburg',
  },
  {
    id: 'city_nsk',
    name: 'Новосибирск',
    region: 'Новосибирская область',
    country: 'Россия',
    lat: 55.0084,
    lon: 82.9357,
    tz: 'Asia/Novosibirsk',
  },
  {
    id: 'city_kzn',
    name: 'Казань',
    region: 'Татарстан',
    country: 'Россия',
    lat: 55.7887,
    lon: 49.1221,
    tz: 'Europe/Moscow',
  },
  {
    id: 'city_minsk',
    name: 'Минск',
    region: null,
    country: 'Беларусь',
    lat: 53.9006,
    lon: 27.559,
    tz: 'Europe/Minsk',
  },
  {
    id: 'city_almaty',
    name: 'Алматы',
    region: null,
    country: 'Казахстан',
    lat: 43.222,
    lon: 76.8512,
    tz: 'Asia/Almaty',
  },
  {
    id: 'city_kiev',
    name: 'Киев',
    region: null,
    country: 'Украина',
    lat: 50.4501,
    lon: 30.5234,
    tz: 'Europe/Kyiv',
  },
  {
    id: 'city_tashkent',
    name: 'Ташкент',
    region: null,
    country: 'Узбекистан',
    lat: 41.2995,
    lon: 69.2401,
    tz: 'Asia/Tashkent',
  },
]

let mockUser: MeResponse = {
  id: 'usr_mock_1',
  tg_id: '1',
  first_name: DEV_USER_NAME || 'Гостья',
  gender: null,
  photo_url: null,
  username: null,
  birth_date: '2001-06-12',
  birth_time: null,
  birth_city_id: null,
  birth_city: null,
  birth_lat: null,
  birth_lon: null,
  birth_tz: null,
  vacation_until: null,
  forecast_time: '09:00',
  forecast_enabled: true,
  link_limit: 5,
  onboarded_at: null,
  terms_version: null,
  last_seen_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
}

/** Мок-пользователь с актуальной отметкой онбординга — её источник правды в LocalStorage. */
const readMockUser = (): MeResponse => ({
  ...mockUser,
  onboarded_at: mockOnboardedAtStorage.getCurrentValue(),
  terms_version: mockTermsVersionStorage.getCurrentValue(),
})

let isRegistered = false

/**
 * Регистрирует все моки слайса. Идемпотентна — повторный вызов (HMR, явный вызов в тестах
 * после `resetMockRegistry`) не плодит дублирующиеся обработчики того же пути дважды за раз.
 */
export function registerUserMocks(): void {
  if (isRegistered) {
    return
  }
  isRegistered = true

  registerMock<void, MeResponse>('GET', API_PATHS.user.me, () => readMockUser())

  registerMock<UpdateMeRequest, MeResponse>('PATCH', API_PATHS.user.me, (input) => {
    mockUser = {
      ...mockUser,
      first_name: input.first_name ?? mockUser.first_name,
      gender: input.gender ?? mockUser.gender,
    }
    return readMockUser()
  })

  registerMock<UpdateBirthRequest, MeResponse>('PATCH', API_PATHS.user.birth, (input) => {
    mockUser = {
      ...mockUser,
      birth_date: input.birth_date,
      birth_time: input.birth_time ?? null,
      birth_city_id: input.birth_city_id ?? null,
      // Бэкенд разворачивает связь `birthCity` — мок обязан вести себя так же,
      // иначе профиль и натальная карта покажут пустое название города.
      birth_city: MOCK_CITIES.find((city) => city.id === input.birth_city_id) ?? null,
    }
    return readMockUser()
  })

  registerMock<UpdateSettingsRequest, MeResponse>('PATCH', API_PATHS.user.settings, (input) => {
    mockUser = {
      ...mockUser,
      forecast_time: input.forecast_time ?? mockUser.forecast_time,
      forecast_enabled: input.forecast_enabled ?? mockUser.forecast_enabled,
    }
    return readMockUser()
  })

  registerMock<UpdateVacationRequest, MeResponse>('PATCH', API_PATHS.user.vacation, (input) => {
    mockUser = {
      ...mockUser,
      vacation_until: input.until,
    }
    return readMockUser()
  })

  // Идемпотентно, как настоящая ручка: дата ставится один раз и повтором не сдвигается.
  registerMock<void, CompleteOnboardingResponse>('POST', API_PATHS.user.onboarding, () => {
    if (!mockOnboardedAtStorage.getCurrentValue()) {
      mockOnboardedAtStorage.setValue(new Date().toISOString())
    }
    return readMockUser()
  })

  // Редакцию ставит «сервер», а не запрос: мок повторяет главное свойство настоящей ручки.
  registerMock<AcceptTermsRequest, AcceptTermsResponse>('POST', API_PATHS.user.terms, () => {
    if (!mockTermsVersionStorage.getCurrentValue()) {
      mockTermsVersionStorage.setValue(TERMS_VERSION)
    }
    return readMockUser()
  })

  registerMock<void, DeleteMeResponse>('DELETE', API_PATHS.user.me, () => ({ ok: true }))

  registerMock<{ q: string }, SearchCitiesResponse>('GET', API_PATHS.city.search, (input) => {
    const query = input.q.trim().toLowerCase()
    return {
      items: MOCK_CITIES.filter((city) => city.name.toLowerCase().startsWith(query)),
    }
  })

  // Пригласившая в моке — женщина: вход B на дефолтных данных должен читаться как в проде.
  // Мужской и «пол не указан» проверяются тестами копирайта, а не подкруткой мока.
  registerMock<{ token: string }, InvitePreviewResponse>('GET', '/invites/:token', (input) => ({
    token: input.token,
    inviter_first_name: 'Настя',
    inviter_gender: 'f',
  }))
}

/**
 * Сбрасывает отметку об онбординге у мок-пользователя — «сервер» снова считает регистрацию
 * незавершённой. Это то, что дёргает кнопка «Онбординг · сбросить» в дев-панели: без бэка
 * обнулить признак больше негде.
 */
export function resetMockOnboarding(): void {
  mockOnboardedAtStorage.removeValue()
  // Акцепт сбрасывается вместе с онбордингом: иначе «сбросить регистрацию» давало бы
  // человека, который заново проходит первый экран, но условия уже принял, — а именно
  // на этом экране строка акцепта и проверяется.
  mockTermsVersionStorage.removeValue()
}

/** @internal Только для тестов — сбрасывает состояние мок-пользователя между кейсами. */
export function resetUserMocksState(): void {
  isRegistered = false
  resetMockOnboarding()
  mockUser = {
    ...mockUser,
    gender: null,
    birth_time: null,
    birth_city_id: null,
    birth_city: null,
  }
}
